// RacesList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink} from 'react-router-dom';
import { Toolbar, AppBar, Grid, ListItemAvatar, Avatar, Divider, ListItemButton, Paper, Box, List, ListItem, ListItemText, useTheme, Typography, TextField, InputAdornment, Pagination, Link } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Wordmark from './components/Wordmark';
import SiteMenu from './components/SiteMenu';

const COMPLETED_PAGE_SIZE = 5;

const apiUrl = process.env.REACT_APP_API_URL;

type Race = {
  directory: string;
  name: string;
  date: string;
  status: 'Live' | 'Upcoming' | 'Complete';
  isExternal: string;
  url: string;
};

const RacesList = () => {
  const theme = useTheme();
  const [races, setRaces] = useState<Race[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [completedPage, setCompletedPage] = useState(1);

  useEffect(() => {
    fetch(`${apiUrl}/api/results/files`)
      .then(response => response.json())
      .then(data => setRaces(data.races.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())));
  }, []);

  const filteredRaces = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query ? races.filter(race => race.name.toLowerCase().includes(query)) : races;
  }, [races, searchQuery]);

  // Reset back to page 1 whenever the search narrows (or widens) the results,
  // so a stale page number can't strand the user on a now-empty page.
  useEffect(() => {
    setCompletedPage(1);
  }, [searchQuery]);

  const liveAndUpcomingRaces = filteredRaces.filter(race => race.status === 'Live' || race.status === 'Upcoming');
  const completedRacesAll = filteredRaces.filter(race => race.status === 'Complete').slice(0).reverse();
  const completedPageCount = Math.max(1, Math.ceil(completedRacesAll.length / COMPLETED_PAGE_SIZE));
  const completedRaces = completedRacesAll.slice(
    (completedPage - 1) * COMPLETED_PAGE_SIZE,
    completedPage * COMPLETED_PAGE_SIZE
  );

  const renderRaceItem = (race: Race) => {
    const isExternal = race.isExternal ===  'true';
    //console.log(race.url)
    return(
      <React.Fragment key={race.directory}>
        <ListItem disablePadding>
          <ListItemAvatar>
            <Avatar alt="Logo" src={`${apiUrl}/result-uploads/${race.directory}/logo.png`} sx = {{ height: '60px', width: '60px', borderRadius: '4px', img: { objectFit: 'contain' } }}/>
          </ListItemAvatar>
          <ListItemButton  sx={{
              '&:hover': {
                bgcolor: 'action.hover', // Use theme's action.hover color for hover background
              },
            }}
            component={isExternal ? 'a' : RouterLink} // Use 'a' for external links
            href={isExternal ? race.url : undefined} // Only set href for external links
            to={!isExternal ? `/race/${race.directory}/results.txt` : undefined} // Only set 'to' for internal navigation
          >
          <ListItemText 
            primary={
              <>
                <Typography component="span" variant="body1" sx={{ fontSize: '15px' }}>
                  {race.name}
                </Typography>
                <Typography component="span" variant="body1" sx={{ fontSize: '13px', fontStyle: 'italic' }}>
                  {" " + race.date}
                </Typography>
              </>
            } 
          />
          </ListItemButton>
        </ListItem>
        <Divider sx={{ opacity: 0.6 }}/> {/* Add a light divider after each item */}
      </React.Fragment>
      )
  };

  return (
    
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
      }}
    >
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Link href="https://timing.kiwi" color="inherit" underline="none" sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={process.env.PUBLIC_URL + '/brand/kiwi-mark-v1-lightgreen-on-dark.svg'} alt="" style={{ height: '48px', marginRight: theme.spacing(2), padding: theme.spacing(1) }} />
              <Wordmark variant="h4" />
            </Link>
          </Box>
          <SiteMenu />
        </Toolbar>
      </AppBar>
        <Paper sx={{ padding: 2, margin: theme.spacing(2) }}>
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events by name"
            sx={{ marginBottom: theme.spacing(2) }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Grid container spacing={2} sx={{ justifyContent: 'space-around' }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ bgcolor: theme.palette.background.paper, margin: theme.spacing(1), padding: theme.spacing(1) }}>
                <Typography variant="h6" gutterBottom>Live & Upcoming</Typography>
                <List dense sx = {{bgcolor: 'background.paper'}}>
                  {liveAndUpcomingRaces.map(race =>
                    renderRaceItem(race)
                  )}
                </List>
                {liveAndUpcomingRaces.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ padding: theme.spacing(1) }}>
                    No races found.
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ bgcolor: theme.palette.background.paper, margin: theme.spacing(1), padding: theme.spacing(1) }}>
                <Typography variant="h6" gutterBottom>Completed</Typography>
                <List dense sx = {{bgcolor: 'background.paper'}}>
                  {completedRaces.map(race =>
                    renderRaceItem(race)
                  )}
                </List>
                {completedRacesAll.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ padding: theme.spacing(1) }}>
                    No races found.
                  </Typography>
                )}
                {completedPageCount > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: theme.spacing(2) }}>
                    <Pagination
                      count={completedPageCount}
                      page={completedPage}
                      onChange={(_event, value) => setCompletedPage(value)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
    </Box>
  );
};

export default RacesList;